import { useOidc } from "@axa-fr/react-oidc";
import { environment } from "@org-avp/avp-avengers-ui-shared-environment";
import { setUserData } from "@org-avp/avp-avengers-ui-shared-store";
import { FC, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { OidcPaths } from "../utils/oidcRoutes";
import { createRedirectUri } from "../utils/oidcUtils";
import { OidcLoggingOut } from "./OidcStateComponents";

const logoutStateKey = "logout_state";

enum LogoutState {
  INITIAL = "initial",
  COGNITO_CALLBACK = "cognito_callback",
  IDP_CALLBACK = "idp_callback",
  FINISHED = "finished",
}

export interface LogoutViewProps {
  oidcPaths: OidcPaths;
}

export const LogoutView: FC<LogoutViewProps> = ({ oidcPaths }) => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { logout, isAuthenticated } = useOidc();
  const navigate = useNavigate();

  useEffect(
    () => {
      if (!isAuthenticated) return;
      const finalizeLogout = () => {
        setSearchParams({ [logoutStateKey]: LogoutState.FINISHED });
        // "no_reload:oidc" prevents the OIDC client from calling the logout endpoint again
        logout(null, { "no_reload:oidc": "true" })
          .then(() => dispatch(setUserData(null)));
      };

      switch (getLogoutState(searchParams)) {
        case LogoutState.INITIAL:
          window.location.href = createCognitoLogoutUrl(`${oidcPaths.logout}?${logoutStateKey}=${LogoutState.COGNITO_CALLBACK}`);
          break;
        case LogoutState.COGNITO_CALLBACK: {
          const idpLogoutUrl = createIdpLogoutUrl(`${oidcPaths.logout}?${logoutStateKey}=${LogoutState.IDP_CALLBACK}`);
          if (idpLogoutUrl != null) window.location.href = idpLogoutUrl;
          else finalizeLogout();
          break;
        }
        case LogoutState.IDP_CALLBACK:
          finalizeLogout();
          break;
      }
    },
    [dispatch, isAuthenticated, logout, oidcPaths.logout, searchParams, setSearchParams],
  );

  useEffect(
    () => {
      if (!isAuthenticated) {
        navigate("/" + oidcPaths.login);
      }
    },
    [isAuthenticated, navigate, oidcPaths.login],
  );

  return (<OidcLoggingOut/>);
};

function createCognitoLogoutUrl(redirectPath: string): string {
  return environment.NX_LOGOUT_URL_COGNITO + encodeURIComponent(createRedirectUri(redirectPath));
}

function createIdpLogoutUrl(redirectPath: string): string | undefined {
  if (environment.NX_LOGOUT_URL_IDP == null) {
    console.log("Skipping IdP logout, no URL provided.");
    return undefined;
  }

  return environment.NX_LOGOUT_URL_IDP + encodeURIComponent(createRedirectUri(redirectPath));
}

function getLogoutState(searchParams: URLSearchParams): LogoutState {
  const logoutState = searchParams.get(logoutStateKey);
  return (logoutState ?? LogoutState.INITIAL) as LogoutState;
}
